import { Box, CircularProgress, Dialog, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useCallback, useEffect, useState } from 'react'
import { apiGet, apiPut } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import AlertUI from '../../generic/AlertUI'
import MosInfoProvider from '../Widgets/common/provider/MosInfoProvider'
import WidgetGrid from '../Widgets/base/WidgetGrid'
import { parseTemplateWidgets } from './widgetTemplateParse'
import { serializeTemplateWidgets } from './widgetTemplateUtils'

const HEADER_HEIGHT = '3.5rem'

/**
 * Full-screen editor that edits the template itself (HU-01): loads the template
 * layout, lets the user change its name and widgets, and saves back to the
 * template via PUT {endpointBase}/{id}. The apply target (device or user) is NOT
 * touched. Defaults keep the device-template behaviour: endpointBase points to
 * the device catalog and the whole widget catalog is offered. Initial-screen
 * templates pass endpointBase='widgets/initialScreenTemplates' and
 * showAllWidgets={false} so only device-independent widgets can be added.
 * onClose(saved) — saved === true when the template was updated.
 */
const EditTemplateDialog = ({ open, template, onClose, endpointBase = 'widgets/templates', showAllWidgets = true }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [name, setName] = useState('')
  const [widgets, setWidgets] = useState([])
  const [editing, setEditing] = useState(true)
  const [loading, setLoading] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    if (!open || !template?.id) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const full = await apiGet(endpointBase + '/' + template.id)
        if (cancelled) return
        setName(full?.name || '')
        setWidgets(parseTemplateWidgets(full?.widgets))
      } catch (e) {
        if (!cancelled) showAlert({ title: t_('error loading widget template'), message: t_(e.message || 'Unknown error'), severity: 'error' })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [open, template, reloadToken, showAlert, t_, endpointBase])

  const reloadWidgets = useCallback(() => setReloadToken((token) => token + 1), [])

  const onSave = useCallback(async () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      showAlert({ title: t_('A name is required'), message: '', severity: 'error' })
      return
    }
    try {
      await apiPut(endpointBase + '/' + template.id, { id: template.id, name: trimmedName, widgets: serializeTemplateWidgets(widgets) })
      showAlert({ title: t_('Template saved'), message: '', severity: 'success' })
      onClose(true)
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    }
  }, [name, widgets, template, showAlert, t_, onClose, endpointBase])

  return (
    <Dialog fullScreen open={open} onClose={() => onClose(false)} PaperProps={{ sx: { overflow: 'hidden' } }}>
      <Stack direction='row' spacing={2} alignItems='center' sx={{ px: 2, height: HEADER_HEIGHT, flexShrink: 0, boxSizing: 'border-box', borderBottom: 1, borderColor: 'divider' }}>
        <Tooltip title={t_('Back')}>
          <IconButton onClick={() => onClose(false)} aria-label={t_('Back')}><ArrowBackIcon /></IconButton>
        </Tooltip>
        <Typography variant='h6' sx={{ whiteSpace: 'nowrap' }}>{t_('Edit template')}</Typography>
        <TextField
          size='small'
          label={t_('Name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          sx={{ minWidth: '16rem' }}
          inputProps={{ maxLength: 255 }}
        />
      </Stack>
      <Box sx={{ height: 'calc(100vh - ' + HEADER_HEIGHT + ')', overflow: 'auto' }}>
        <Stack direction='column' sx={{ height: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            <div style={{ position: 'sticky', top: '0.5rem', zIndex: 100, padding: '0 1rem' }}>{AlertElement}</div>
            {loading
              ? <Stack justifyContent='center' alignItems='center' sx={{ height: '100%' }}><CircularProgress size='6rem' /></Stack>
              : (
                <MosInfoProvider>
                  <WidgetGrid
                    widgets={widgets}
                    setWidgets={setWidgets}
                    reloadWidgets={reloadWidgets}
                    onSave={onSave}
                    editing={editing}
                    setEditing={setEditing}
                    showAllWidgets={showAllWidgets}
                    toolbarLabel={name}
                  />
                </MosInfoProvider>
                )}
          </Box>
        </Stack>
      </Box>
    </Dialog>
  )
}

export default EditTemplateDialog
